import { Trans } from '@lingui/macro';
import { observer } from 'mobx-react-lite';
import React, { FC, useState, useEffect, useContext } from 'react'
import { BallTriangle } from 'react-loader-spinner';
import { Context } from '../..';
import DocumentsService from '../../services/DocumentsService';
import { ModalPopover } from './modal/modal-popover';
import { DocsEditModal } from "./modal/DocsEditModal";
import { API_URL } from '../../appConfig';

const Doc: FC<any> = observer(({ doc, setDelDocId, setDelDocModal }) => {
  const { store } = useContext(Context)
  const [docEditModal, setDocEditModal] = useState<boolean>(false);

  return (
    <div className='row m-3'>
      <hr />
      <div className="col">
        <a href={API_URL + doc['url']} className="doc-link" target="_blank" rel="noreferrer" download>
          <div className="h6">{doc['name']}</div>
          <div>
            <span className="small me-1 text-primary"><Trans>Date</Trans>:</span>
            <span className="small me-1">{doc['date']}</span>
            <span className="small me-1 text-primary"><Trans>Chapter</Trans>:</span>
            <span className="small me-1">{doc['chapter']['chapter']}</span>
            <span className="small me-1 text-primary"><Trans>Publisher</Trans>:</span>
            <span className="small me-1">{doc['publisher']['publisher']}</span>
            <span className="small me-1 text-primary"><Trans>Status</Trans>:</span>
            <span className="small me-1">{doc['status']['status']}</span>
          </div>
        </a>
      </div>
      {store.user.is_superuser ?
        <div className="col-md-1">
          <i className='icon admin ms-3' onClick={() => setDocEditModal(true)} />
          <i className='icon trash' onClick={() => { setDelDocId(doc['id']); setDelDocModal(true); }} />
        </div>
        : null}
      <DocsEditModal doc={doc} toggle={docEditModal} setToggle={setDocEditModal} />
    </div>
  )
});

/* ---------------- ФИЛЬТР НЕНУЖНЫХ ПОЗИЦИЙ ---------------- */

// Нормализация заголовка: убираем неразрывные пробелы, схлопываем пробелы,
// приводим кавычки к обычным, переводим в ВЕРХНИЙ РЕГИСТР и trim.
function normalizeTitle(s: any): string {
  return String(s ?? '')
    .replace(/\u00A0/g, ' ')            // NBSP -> space
    .replace(/[«»„“”]/g, '"')          // русские/типографские кавычки -> "
    .replace(/\s+/g, ' ')               // множественные пробелы -> один
    .toUpperCase()
    .trim();
}

// Выдёргиваем дату в формате YYYY-MM-DD из любой строки
function extractYmd(dateStr: any): string {
  const s = String(dateStr ?? '');
  const m = s.match(/\d{4}-\d{2}-\d{2}/);
  if (m) return m[0];
  return s.slice(0, 10); // fallback
}

// 3 сборника — общий заголовок и точные даты
const LITS_MOVED_BASE = normalizeTitle(
  'СБОРНИК МЕТОДИЧЕСКИХ РАЗРАБОТОК ПО ОБУЧЕНИЮ ДЕТЕЙ С РАССТРОЙСТВАМИ АУТИСТИЧЕСКОГО СПЕКТРА (РАС) ДОШКОЛЬНОГО И ШКОЛЬНОГО ВОЗРАСТА'
);
const LITS_MOVED_DATES = new Set(['2025-01-13', '2025-01-20', '2025-01-30']);

// Остальные элементы, которые нужно скрыть
const TITLES_TO_HIDE_EXACT = new Set([
  normalizeTitle('ЗОНАЛЬНЫЙ ПЕДАГОГИЧЕСКИЙ СЕМИНАР «СОВРЕМЕННЫЕ ТЕХНОЛОГИИ И ПОДХОДЫ В РАБОТЕ С ОБУЧАЮЩИМИСЯ С РАС»'),
  normalizeTitle('Церемония поднятия флагов'),
  normalizeTitle('Фестиваль4'),
  normalizeTitle('Фестиваль 2'),
  normalizeTitle('Гульсия1'),
  normalizeTitle('Гульсия'),
  // на случай, если это попадает как отдельный «документ»-объявление
  normalizeTitle('С уважением, кафедра специального и инклюзивного образования ГАУ ДПО Институт развития образования Республики Башкортостан'),
]);

function shouldHideParentsLit(item: any): boolean {
  const titleNorm = normalizeTitle(item?.name ?? item?.title);
  const ymd = extractYmd(item?.date ?? item?.publish_date);

  // 1) три сборника
  if (titleNorm.includes(LITS_MOVED_BASE) && LITS_MOVED_DATES.has(ymd)) {
    return true;
  }
  // 2) по точным названиям (нормализованным)
  if (TITLES_TO_HIDE_EXACT.has(titleNorm)) {
    return true;
  }
  return false;
}
/* ---------------- КОНЕЦ ФИЛЬТРА ---------------- */

const ForParentsLits: FC = () => {
  const { store } = useContext(Context)
  const [documents, setDocuments] = useState<any[]>([]);
  const [delDocId, setDelDocId] = useState<number>(-1);
  const [delDocModal, setDelDocModal] = useState<boolean>(false);
  const [fix, setFix] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const getDocs = async () => {
    const res = await DocumentsService.getDocsByType(2, 0);
    const results = Array.isArray(res?.data?.results) ? res.data.results : [];
    // применяем фильтр перед показом
    const filtered = results.filter((d: any) => !shouldHideParentsLit(d));
    setDocuments(filtered);
    setLoader(false);
  }

  useEffect(() => {
    setLoader(true);
    getDocs();
    setFix(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fix]);

  const deleteDoc = async () => {
    await DocumentsService.removeDocument(delDocId);
    setFix(true);
    setDelDocModal(false);
    setDelDocId(-1);
  };

  const getDocuments = () =>
    [...documents] // не мутируем state
      .sort((a: any, b: any) => (b['id'] - a['id']))
      .map((doc: any, i: number) => (
        <Doc key={i} {...{ doc, setDelDocId, setDelDocModal }} />
      ));

  return (loader ? (
    <div className='row justify-content-center'>
      <div className='col-md-2 mt-5'>
        <BallTriangle color="#1d7ea7" height={90} width={90} />
      </div>
    </div>
  ) : (
    <div>
      <h6 className='p-3 text-center'>Литература для родителей</h6>
      <div className="row form-group">
        {getDocuments()}
      </div>

      <ModalPopover
        toggle={delDocModal}
        setToggle={setDelDocModal}
        title={store.lang === 'ba' ? 'Файлды алып ташлау' : store.lang === 'ru' ? 'Удаление файла' : 'Deleting a file'}
        modalContent={<Trans>Do you want to delete a file?</Trans>}
        clickedButtonOne={() => deleteDoc()}
        clickedButtonTwo={() => { setDelDocModal(false); setDelDocId(-1); }}
        ButtonOneValue={store.lang === 'ba' ? 'Эйе' : store.lang === 'ru' ? 'Да' : 'Yes'}
        ButtonTwoValue={store.lang === 'ba' ? 'Кире ҡайтарыу' : store.lang === 'ru' ? 'Отмена' : 'Cancel'}
      />
    </div>
  ));
};

export default observer(ForParentsLits);
